import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import styles from '../styles/GroupPage.module.css';
import GroupInfo from './GroupInfo';
import { trpc } from '../utils/trpc';
import MemberProfileModal from '../components/MemberProfileModal';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ReactDOM from 'react-dom';
import AllMembersModal from './AllMembersModal';
import NotificationSender from '../components/NotificationSender';
import { useBannerStore } from '../utils/banner';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EventRow {
  event_id: string;
  event_name: string;
  date: string;
  duration_minutes: number | null;
  max_capacity: number | null;
  description: string | null;
  venue_id: string | null;
  status: string | null;
  event_tags?: { tags: { name: string } }[];
  event_attendees?: { count: number }[];
}

type SortOption = 'new' | 'top';

// ─── Time helpers ─────────────────────────────────────────────────────────────

/** Format a Date to 12-hour time string, e.g. "7:00 PM" */
function fmt12(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** Abbreviated month from a date, e.g. "Mar" */
function fmtMonth(date: Date): string {
  return date.toLocaleString('en-US', { month: 'short' });
}

/** Day of month as string, e.g. "15" */
function fmtDay(date: Date): string {
  return String(date.getDate());
}

// ─── VenueName ───────────────────────────────────────────────────────────────
// Fetches and displays a venue name by venue_name field.
// Renders inline so it fits naturally inside card meta rows.

function VenueName({ venueId }: { venueId: string | null }) {
  // First fetch the venue to get its venue_name slug, then look it up
  const { data: venueData } = trpc.venue.getById.useQuery(
    { venue_id: venueId! },
    { enabled: !!venueId },
  );

  if (!venueId) return <span>No venue</span>;
  if (!venueData) return <span>Loading...</span>;
  return <span>{venueData.venue_name}</span>;
}

// ─── Shared Footer ────────────────────────────────────────────────────────────

interface FooterProps {
  event: EventRow;
  pinned?: boolean;
}

function CardFooter({ event, pinned = false }: FooterProps) {
  const utils = trpc.useUtils();
  const attendeeCount = event.event_attendees?.[0]?.count ?? 0;
  const [rsvpCount, setRsvpCount] = useState(attendeeCount);

  // Fetch the current user's RSVP status from the server to initialise correctly
  const { data: myRsvp } = trpc.event.getMyRsvp.useQuery(
    { eventId: event.event_id },
    { retry: false },
  );

  // Optimistic state — initialised from server once loaded
  const [rsvped, setRsvped] = useState<boolean | null>(null);

  // Sync server state into local once it arrives
  const serverRsvped = myRsvp !== undefined ? myRsvp?.status === 'going' : null;
  const isRsvped = rsvped !== null ? rsvped : (serverRsvped ?? false);

  const rsvpMutation = trpc.event.rsvpToEvent.useMutation({
    onSuccess: () => {
      setRsvpCount((c) => c + 1);
      utils.event.getEventsByGroupId.invalidate();
      utils.event.getMyRsvp.invalidate({ eventId: event.event_id });
    },
    onError: (err) => {
      console.error('RSVP failed:', err.message);
      setRsvped(false); // roll back optimistic
    },
  });

  const cancelRsvpMutation = trpc.event.cancelRsvp.useMutation({
    onSuccess: () => {
      setRsvpCount((c) => Math.max(0, c - 1));
      utils.event.getEventsByGroupId.invalidate();
      utils.event.getMyRsvp.invalidate({ eventId: event.event_id });
    },
    onError: (err) => {
      console.error('Cancel RSVP failed:', err.message);
      setRsvped(true); // roll back optimistic
    },
  });

  const handleRsvp = () => {
    if (rsvpMutation.isPending || cancelRsvpMutation.isPending) return;
    if (isRsvped) {
      setRsvped(false);
      cancelRsvpMutation.mutate({ eventId: event.event_id });
    } else {
      setRsvped(true);
      rsvpMutation.mutate({ eventId: event.event_id, status: 'going' });
    }
  };

  const isPending = rsvpMutation.isPending || cancelRsvpMutation.isPending;
  const footerClass = pinned ? styles.pinnedFooter : styles.cardFooter;

  return (
    <div className={footerClass}>
      <button
        data-testid="rsvp-button"
        className={`${styles.rsvpBtn} ${isRsvped ? styles.on : styles.off}`}
        onClick={handleRsvp}
        disabled={isPending}
      >
        {isRsvped ? '✓ Going' : 'RSVP'}
        <span data-testid="rsvp-count" className={styles.rsvpCount}>
          · {rsvpCount}
        </span>
      </button>

      <button data-testid="comments-button" className={styles.actionBtn}>
        Comments
      </button>
      <button data-testid="share-button" className={styles.actionBtn}>
        Share
      </button>
    </div>
  );
}

// ─── Post Card ────────────────────────────────────────────────────────────────

interface PostCardProps {
  event: EventRow;
  animationDelay?: string;
  pinned?: boolean;
}

function PostCard({ event, animationDelay, pinned = false }: PostCardProps) {
  const date = new Date(event.date);
  const stubDay = fmtDay(date);
  const stubMonth = fmtMonth(date);
  const timeStr = fmt12(date);

  if (pinned) {
    return (
      <div className={`${styles.postCard} ${styles.pinned}`} style={{ animationDelay }}>
        <div className={styles.pinnedBand}>
          <div className={styles.pinnedBandDate}>
            <span className={styles.pinnedBandDay}>{stubDay}</span>
            <div className={styles.pinnedBandMonthTime}>
              <span className={styles.pinnedBandMonth}>
                {stubMonth} {date.getFullYear()}
              </span>
              <span className={styles.pinnedBandTime}>{timeStr}</span>
            </div>
          </div>
          <div className={styles.pinnedBandDivider} />
          <span className={styles.pinnedBandLocation}>
            <VenueName venueId={event.venue_id} />
          </span>
          <div className={styles.pinnedBandRight}>
            <span className={styles.pinnedChip}>Pinned</span>
          </div>
        </div>

        <div className={styles.pinnedContent}>
          <h3 className={styles.pinnedTitle}>{event.event_name}</h3>
          {event.description && (
            <p data-testid="event-description" className={styles.pinnedText}>
              {event.description}
            </p>
          )}
          <CardFooter event={event} pinned />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.postCard} style={{ animationDelay }}>
      <div className={styles.cardStub}>
        <span className={styles.stubDay}>{stubDay}</span>
        <span className={styles.stubMonth}>{stubMonth}</span>
        <div className={styles.stubDot} />
      </div>

      <div className={styles.cardContent}>
        <div className={styles.cardHeader}>
          <div className={styles.headerChips}>
            <span className={styles.eventChip}>Event</span>
          </div>
        </div>
        <h3 className={styles.postTitle}>{event.event_name}</h3>
        {event.description && (
          <p data-testid="event-description" className={styles.postText}>
            {event.description}
          </p>
        )}
        <div className={styles.cardMeta}>
          <div className={styles.cardMetaItem}>
            <span className={styles.cardMetaLabel}>Venue</span>
            <span className={styles.cardMetaValue}>
              <VenueName venueId={event.venue_id} />
            </span>
          </div>
          <div className={styles.cardMetaItem}>
            <span className={styles.cardMetaLabel}>Time</span>
            <span className={styles.cardMetaValue}>{timeStr}</span>
          </div>
          {event.max_capacity && (
            <div className={styles.cardMetaItem}>
              <span className={styles.cardMetaLabel}>Capacity</span>
              <span className={styles.cardMetaValue}>{event.max_capacity}</span>
            </div>
          )}
          {event.event_tags && event.event_tags.length > 0 && (
            <div className={styles.cardMetaItem}>
              <span className={styles.cardMetaLabel}>Interests</span>
              <span className={styles.cardMetaValue}>
                {event.event_tags.map((t) => t.tags.name).join(', ')}
              </span>
            </div>
          )}
        </div>
        <CardFooter event={event} />
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function GroupPage() {
  const { id } = useParams<{ id: string; groupname: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isMember, isLoading, user } = useAuth();
  const [memberCount, setMemberCount] = useState(0);
  const [sortBy, setSortBy] = useState<SortOption>('new');
  const [showInfo, setShowInfo] = useState(false);
  const [selectedMember, setSelectedMember] = useState<string | null>(null);
  const [joinPrompt, setJoinPrompt] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [newOwnerId, setNewOwnerId] = useState<string>('');
  const joinBtnRef = useRef<HTMLButtonElement>(null);
  const [popoutPos, setPopoutPos] = useState({ top: 0, right: 0 });
  const [showAllMembers, setShowAllMembers] = useState(false);

  const [optimisticJoined, setOptimisticJoined] = useState<boolean | null>(null);
  const [optimisticRequestSent, setOptimisticRequestSent] = useState<boolean | null>(
    null,
  );

  const {
    data: group,
    isLoading: groupLoading,
    error,
  } = trpc.group.getById.useQuery({ id: id! }, { enabled: !!id });

  const showBanner = useBannerStore((state) => state.showBanner);
  const clearBanner = useBannerStore((state) => state.clearBanner);

  useEffect(() => {
    showBanner({
      icon: group?.profile_picture_url,
      header: 'Group: ' + group?.name,
      buttons: {
        'View/Manage Events': '/groups/' + id + '/events',
      },
      selected: 'View/Manage Events',
    });
    return () => clearBanner();
  }, [group]);

  const { data: members = [] } = trpc.group.getMembers.useQuery(
    { groupId: id! },
    { enabled: !!id },
  );

  const { data: myStatus } = trpc.group.getMyStatus.useQuery(
    { groupId: id! },
    { enabled: !!id && !!user },
  );

  // ── Real events from API ──────────────────────────────────────────────────
  const { data: allEvents = [], isLoading: eventsLoading } =
    trpc.event.getEventsByGroupId.useQuery(
      { groupId: id!, upcomingOnly: true },
      { enabled: !!id },
    );

  // Only show Accepted events (or events with no venue which don't need approval)
  const visibleEvents = (allEvents as EventRow[]).filter(
    (e) => e.status === 'Accepted' || e.status === null,
  );

  // Sort events based on active tab
  const sortedEvents = [...visibleEvents].sort((a, b) => {
    if (sortBy === 'new') {
      // Most recently created first — fall back to date if no created_at
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    }
    // Top: most attendees first
    const aCount = a.event_attendees?.[0]?.count ?? 0;
    const bCount = b.event_attendees?.[0]?.count ?? 0;
    return bCount - aCount;
  });

  // First event is pinned if there are any
  const pinnedEvent = sortedEvents[0] ?? null;
  const feedEvents = sortedEvents.slice(1);

  const joined = optimisticJoined ?? myStatus?.status === 'joined';
  const requestSent = optimisticRequestSent ?? myStatus?.status === 'requested';

  useEffect(() => {
    if (joinPrompt && joinBtnRef.current) {
      const rect = joinBtnRef.current.getBoundingClientRect();
      setPopoutPos({
        top: rect.bottom + 10,
        right: window.innerWidth - rect.right,
      });
    }
  }, [joinPrompt]);

  const resolvedMemberCount =
    memberCount !== 0 ? memberCount : (group?.group_members?.[0]?.count ?? 0);

  const utils = trpc.useUtils();

  const joinMutation = trpc.group.join.useMutation({
    onSuccess: (data) => {
      setOptimisticJoined(null);
      setOptimisticRequestSent(null);
      if (!data.requested) {
        setMemberCount(resolvedMemberCount + 1);
        utils.group.getMembers.invalidate({ groupId: id! });
        utils.group.getById.invalidate({ id: id! });
      }
      utils.group.getMyStatus.invalidate({ groupId: id! });
    },
    onError: (err) => {
      setOptimisticJoined(null);
      setOptimisticRequestSent(null);
      console.error(err.message);
    },
  });

  const leaveMutation = trpc.group.leave.useMutation({
    onSuccess: (data) => {
      setOptimisticJoined(null);
      setOptimisticRequestSent(null);
      setNewOwnerId('');

      if (data && 'groupDeleted' in data && data.groupDeleted) {
        navigate('/groups/popular');
        return;
      }

      setMemberCount(resolvedMemberCount - 1);
      utils.group.getMembers.invalidate({ groupId: id! });
      utils.group.getById.invalidate({ id: id! });
      utils.group.getMyStatus.invalidate({ groupId: id! });
    },
    onError: (err) => {
      setOptimisticJoined(null);
      setOptimisticRequestSent(null);
      if (err.message.toLowerCase().includes('transfer ownership')) {
        alert('You must select a new owner before leaving this group.');
      } else {
        console.error(err.message);
      }
    },
  });

  const currentUserRole = members.find((m) => m.profile_id === user?.id)?.role;
  const isOrganizer = currentUserRole === 'owner' || currentUserRole === 'admin';
  const canSendNotifications = currentUserRole === 'owner' || currentUserRole === 'admin';
  const otherMembers = members.filter((m) => m.profile_id !== user?.id);
  const ownerMustTransfer = currentUserRole === 'owner' && otherMembers.length > 0;
  const ownerCanDeleteByLeaving =
    currentUserRole === 'owner' && otherMembers.length === 0;

  const handleJoin = () => {
    if (isLoading) return;
    if (!isAuthenticated || !isMember) {
      setJoinPrompt((p) => !p);
      return;
    }
    setJoinPrompt(false);
    if (joined) {
      setNewOwnerId('');
      setShowLeaveConfirm(true);
    } else if (!requestSent) {
      if (group?.privacy === 'private') {
        setOptimisticRequestSent(true);
      } else {
        setOptimisticJoined(true);
      }
      joinMutation.mutate({ groupId: id! });
    }
  };

  if (groupLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner} />
          Loading group...
        </div>
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState}>Group not found.</div>
      </div>
    );
  }

  const tags =
    group.group_tags?.map((t: { tags: { name: string } }) => t.tags.name) ?? [];

  const avatarInitial = group.name?.charAt(0).toUpperCase() ?? 'G';

  if (group.privacy === 'private' && !joined) {
    return (
      <div className={styles.page}>
        <div className={styles.hero}>
          <div className={`${styles.heroRing} ${styles.heroRing1}`} />
          <div className={`${styles.heroRing} ${styles.heroRing2}`} />
          <div className={`${styles.heroRing} ${styles.heroRing3}`} />
          <div className={styles.heroStripe}>
            <div className="gsShimmerBar" />
          </div>

          <div className={styles.heroIdentity}>
            <button className={styles.heroAvatar} aria-label={`View ${group.name} info`}>
              {group.profile_picture_url ? (
                <img
                  onLoad={(e) => e.currentTarget.classList.add('loaded')}
                  className={styles.heroAvatarImage}
                  src={group.profile_picture_url}
                  alt="Group avatar"
                />
              ) : (
                avatarInitial
              )}
            </button>

            <div className={styles.heroText}>
              <h1 className={styles.heroGroupName}>{group.name}</h1>
              <span className={styles.heroHandle}>
                gs/{group.name?.toLowerCase().replace(/\s+/g, '-')}
              </span>
            </div>

            <div className={styles.heroRight}>
              <div className={styles.heroStats}>
                {[
                  { value: resolvedMemberCount.toLocaleString(), label: 'Members' },
                  { value: group.postCount ?? '—', label: 'Posts' },
                  {
                    value: group.created_at
                      ? new Date(group.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          year: 'numeric',
                        })
                      : '—',
                    label: 'Founded',
                  },
                ].map(({ value, label }) => (
                  <div key={label} className={styles.heroStat}>
                    <div className={styles.heroStatValue}>{value}</div>
                    <div className={styles.heroStatLabel}>{label}</div>
                  </div>
                ))}
              </div>
              <div className={styles.joinWrapper}>
                <button
                  ref={joinBtnRef}
                  className={`${styles.joinBtn} ${requestSent ? styles.requested : styles.off}`}
                  onClick={handleJoin}
                  disabled={isLoading || joinMutation.isPending || requestSent}
                >
                  {joinMutation.isPending
                    ? '...'
                    : requestSent
                      ? 'Requested'
                      : 'Request to Join'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.aboutStrip}>
          <div className={styles.aboutStripInner}>
            <p className={styles.aboutDesc}>
              {group.description ?? 'No description provided.'}
            </p>
            <div className={styles.aboutDivider} />
            <div className={styles.aboutStats}>
              {[
                { value: resolvedMemberCount.toLocaleString(), label: 'Members' },
                { value: group.postCount ?? '—', label: 'Posts' },
                {
                  value: group.created_at
                    ? new Date(group.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        year: 'numeric',
                      })
                    : '—',
                  label: 'Founded',
                },
                { value: 'Private', label: 'Visibility' },
              ].map(({ value, label }) => (
                <div key={label} className={styles.aboutStat}>
                  <div className={styles.aboutStatVal}>{value}</div>
                  <div className={styles.aboutStatLbl}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.privateGate}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <rect
              x="3"
              y="11"
              width="18"
              height="11"
              rx="2"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M7 11V7a5 5 0 0 1 10 0v4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <h2 className={styles.privateGateTitle}>This is a private group</h2>
          <p className={styles.privateGateText}>
            {requestSent
              ? 'Your request is pending approval from an admin.'
              : 'Join to see posts, members, and everything else.'}
          </p>
        </div>

        {joinPrompt &&
          ReactDOM.createPortal(
            <div
              className={styles.joinPopout}
              style={{ top: popoutPos.top, right: popoutPos.right }}
            >
              <p className={styles.joinPopoutText}>
                {isAuthenticated && !isMember
                  ? 'You need a member account to join groups'
                  : 'Sign in to join this group'}
              </p>
              <div className={styles.joinPopoutBtns}>
                <button
                  className={styles.joinPopoutSignIn}
                  onClick={() => navigate('/sign-in')}
                >
                  Sign In
                </button>
                <button
                  className={styles.joinPopoutSignUp}
                  onClick={() => navigate('/sign-up')}
                >
                  Sign Up
                </button>
              </div>
            </div>,
            document.body,
          )}
      </div>
    );
  }

  const adminList = members
    .filter((m) => m.role === 'owner' || m.role === 'admin' || m.role === 'moderator')
    .map((m) => {
      const profile = Array.isArray(m.member_profiles)
        ? m.member_profiles[0]
        : m.member_profiles;
      return {
        name: profile?.username ?? 'Unknown',
        avatar: profile?.username?.charAt(0).toUpperCase() ?? '?',
        role: (m.role === 'owner'
          ? 'Owner'
          : m.role === 'moderator'
            ? 'Mod'
            : 'Admin') as 'Owner' | 'Admin' | 'Mod',
      };
    });

  return (
    <div className={styles.page}>
      {/* ── Hero ── */}
      <div className={styles.hero}>
        <div className={`${styles.heroRing} ${styles.heroRing1}`} />
        <div className={`${styles.heroRing} ${styles.heroRing2}`} />
        <div className={`${styles.heroRing} ${styles.heroRing3}`} />
        <div className={styles.heroStripe}>
          <div className="gsShimmerBar" />
        </div>

        <div className={styles.heroIdentity}>
          <button
            className={styles.heroAvatar}
            onClick={() => setShowInfo(true)}
            aria-label={`View ${group.name} group info`}
          >
            {group.profile_picture_url ? (
              <img
                onLoad={(e) => e.currentTarget.classList.add('loaded')}
                className={styles.heroAvatarImage}
                src={group.profile_picture_url}
                alt="Group avatar"
              />
            ) : (
              avatarInitial
            )}
          </button>

          <div className={styles.heroText}>
            <h1 className={styles.heroGroupName}>{group.name}</h1>
            <span className={styles.heroHandle}>
              gs/{group.name?.toLowerCase().replace(/\s+/g, '-')}
            </span>
          </div>

          <div className={styles.heroRight}>
            <div className={styles.heroStats}>
              {[
                { value: resolvedMemberCount.toLocaleString(), label: 'Members' },
                { value: visibleEvents.length, label: 'Events' },
                {
                  value: group.created_at
                    ? new Date(group.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        year: 'numeric',
                      })
                    : '—',
                  label: 'Founded',
                },
              ].map(({ value, label }) => (
                <div key={label} className={styles.heroStat}>
                  <div className={styles.heroStatValue}>{value}</div>
                  <div className={styles.heroStatLabel}>{label}</div>
                </div>
              ))}
            </div>

            <div className={styles.joinWrapper}>
              <button
                ref={joinBtnRef}
                className={`${styles.joinBtn} ${joined ? styles.on : styles.off}`}
                onClick={handleJoin}
                disabled={isLoading || joinMutation.isPending || leaveMutation.isPending}
              >
                {joinMutation.isPending || leaveMutation.isPending
                  ? '...'
                  : joined
                    ? 'Leave Group'
                    : 'Join Group'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── About Strip ── */}
      <div className={styles.aboutStrip}>
        <div className={styles.aboutStripInner}>
          <p className={styles.aboutDesc}>
            {group.description ?? 'No description provided.'}
          </p>
          <div className={styles.aboutDivider} />
          <div className={styles.aboutStats}>
            {[
              { value: resolvedMemberCount.toLocaleString(), label: 'Members' },
              { value: visibleEvents.length, label: 'Events' },
              {
                value: group.created_at
                  ? new Date(group.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      year: 'numeric',
                    })
                  : '—',
                label: 'Founded',
              },
              { value: group.privacy ?? 'Public', label: 'Visibility' },
            ].map(({ value, label }) => (
              <div key={label} className={styles.aboutStat}>
                <div className={styles.aboutStatVal}>{value}</div>
                <div className={styles.aboutStatLbl}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Two-column layout ── */}
      <div className={styles.layout}>
        <div className={styles.feedColumn}>
          <div className={styles.feedControls}>
            {sortedEvents.length > 0 && (
              <div className={styles.sortBar}>
                {(['new', 'top'] as SortOption[]).map((s) => (
                  <button
                    key={s}
                    className={`${styles.sortBtn} ${sortBy === s ? styles.active : ''}`}
                    onClick={() => setSortBy(s)}
                  >
                    {s === 'new' ? 'New' : 'Top'}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Events feed */}
          {eventsLoading && (
            <div className={styles.loadingState}>
              <div className={styles.loadingSpinner} />
            </div>
          )}

          {!eventsLoading && sortedEvents.length === 0 && (
            <div className={styles.emptyFeed}>
              {isOrganizer ? (
                <>
                  <p>No events yet.</p>
                  <p>
                    You can create one from the{' '}
                    <button
                      className={styles.linkBtn}
                      onClick={() => navigate(`/groups/${id}/events`)}
                    >
                      Events Management Page
                    </button>
                    .
                  </p>
                </>
              ) : (
                <>
                  <p>No events yet.</p>
                  <p>
                    You’ll be able to see events here once an organizer posts one. In the
                    meantime, you can check the{' '}
                    <button
                      className={styles.linkBtn}
                      onClick={() => navigate(`/groups/${id}/events`)}
                    >
                      events page
                    </button>{' '}
                    for past activity.
                  </p>
                </>
              )}
            </div>
          )}

          {/* First event is pinned */}
          {pinnedEvent && (
            <PostCard
              key={pinnedEvent.event_id}
              event={pinnedEvent}
              pinned
              animationDelay="0s"
            />
          )}

          {/* Rest of the feed */}
          {feedEvents.map((event, i) => (
            <PostCard
              key={event.event_id}
              event={event}
              animationDelay={`${0.07 * (i + 1)}s`}
            />
          ))}
        </div>

        <aside className={styles.sidebar}>
          <div className={styles.sidebarCard}>
            <div className={styles.cardLabel}>
              <div className={styles.cardLabelDot} />
              <h2 className={styles.cardLabelText}>Group Events</h2>
              <button
                className={styles.viewAllBtn}
                onClick={() => navigate(`/groups/${group.id}/events`)}
              >
                View/Manage Events
              </button>
            </div>
          </div>
          {/* Tags */}
          <div className={styles.sidebarCard}>
            <div className={styles.cardLabel}>
              <div className={styles.cardLabelDot} />
              <h2 className={styles.cardLabelText}>Tags</h2>
            </div>
            <div className={styles.tagsBody}>
              {tags.length > 0 ? (
                tags.map((tag: string) => (
                  <span key={tag} className={styles.tag}>
                    {tag}
                  </span>
                ))
              ) : (
                <span className={styles.emptyState}>No interests yet</span>
              )}
            </div>
          </div>

          {/* Members */}
          <div className={styles.sidebarCard}>
            <div className={styles.cardLabel}>
              <div className={styles.cardLabelDot} />
              <h2 className={styles.cardLabelText}>Members</h2>
            </div>
            <div className={styles.membersBody}>
              {members.map((member) => {
                const profile = Array.isArray(member.member_profiles)
                  ? member.member_profiles[0]
                  : member.member_profiles;
                const initial = profile?.username?.charAt(0).toUpperCase() ?? '?';
                const role =
                  member.role === 'owner'
                    ? 'Owner'
                    : member.role === 'admin'
                      ? 'Admin'
                      : member.role === 'moderator'
                        ? 'Moderator'
                        : 'Member';

                return (
                  <div
                    key={profile?.id ?? member.profile_id}
                    className={styles.memberRow}
                    onClick={() =>
                      profile?.username && setSelectedMember(profile.username)
                    }
                    style={{ cursor: 'pointer' }}
                  >
                    {profile?.profile_picture_url ? (
                      <img
                        onLoad={(e) => e.currentTarget.classList.add('loaded')}
                        src={profile.profile_picture_url}
                        alt={profile.username}
                        className={styles.memberAvatar}
                      />
                    ) : (
                      <div className={styles.memberAvatar}>{initial}</div>
                    )}
                    <span className={styles.memberName}>
                      {profile?.username ?? 'Unknown'}
                    </span>
                    <span
                      className={`${styles.roleBadge} ${
                        styles[
                          role.toLowerCase() as 'owner' | 'admin' | 'moderator' | 'member'
                        ]
                      }`}
                    >
                      {role}
                    </span>
                  </div>
                );
              })}
              <button
                className={styles.viewAllBtn}
                onClick={() => setShowAllMembers(true)}
              >
                View all {resolvedMemberCount} members →
              </button>
            </div>
          </div>

          {canSendNotifications && <NotificationSender groupId={id} />}

          {canSendNotifications && (
            <button
              className={styles.manageRequestsBtn}
              onClick={() => navigate(`/group/${id}/requests`)}
            >
              Manage Join Requests
            </button>
          )}
        </aside>
      </div>

      {/* ── GroupInfo modal ── */}
      {showInfo && (
        <GroupInfo
          group={{
            name: group.name,
            handle: group.name?.toLowerCase().replace(/\s+/g, '-'),
            avatar: avatarInitial,
            description: group.description ?? '',
            memberCount: resolvedMemberCount,
            postCount: allEvents.length,
            founded: group.created_at
              ? new Date(group.created_at).toLocaleDateString('en-US', {
                  month: 'short',
                  year: 'numeric',
                })
              : '—',
            tags,
            isJoined: joined,
            bannerUrl: group.banner_url ?? undefined,
            profile_picture_url: group.profile_picture_url ?? undefined,
            rules: [],
            admins: adminList,
          }}
          onClose={() => setShowInfo(false)}
        />
      )}

      {selectedMember && (
        <MemberProfileModal
          username={selectedMember}
          onClose={() => setSelectedMember(null)}
        />
      )}

      {joinPrompt &&
        ReactDOM.createPortal(
          <div
            className={styles.joinPopout}
            style={{ top: popoutPos.top, right: popoutPos.right }}
          >
            <p className={styles.joinPopoutText}>
              {isAuthenticated && !isMember
                ? 'You need a member account to join groups'
                : 'Sign in to join this group'}
            </p>
            <div className={styles.joinPopoutBtns}>
              <button
                className={styles.joinPopoutSignIn}
                onClick={() => navigate('/sign-in')}
              >
                Sign In
              </button>
              <button
                className={styles.joinPopoutSignUp}
                onClick={() => navigate('/sign-up')}
              >
                Sign Up
              </button>
            </div>
          </div>,
          document.body,
        )}

      {showAllMembers && (
        <AllMembersModal
          members={members}
          onSelectMember={(username) => {
            setSelectedMember(username);
            setShowAllMembers(false);
          }}
          groupId={id!}
          onClose={() => setShowAllMembers(false)}
        />
      )}

      {/* ── Leave Confirm Modal ── */}
      {showLeaveConfirm &&
        ReactDOM.createPortal(
          <div
            data-testid="leave-overlay"
            className={styles.leaveOverlay}
            onClick={() => {
              setShowLeaveConfirm(false);
              setNewOwnerId('');
            }}
          >
            <div className={styles.leaveModal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.leaveIcon}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 className={styles.leaveTitle}>Leave group?</h2>
              {ownerMustTransfer ? (
                <>
                  <p className={styles.leaveBody}>
                    You're the owner of <strong>{group.name}</strong>. Select a member to
                    become the new owner before leaving.
                  </p>
                  <select
                    value={newOwnerId}
                    onChange={(e) => setNewOwnerId(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      marginTop: '10px',
                      borderRadius: '6px',
                      border: '1px solid #ccc',
                      marginBottom: '10px',
                    }}
                  >
                    <option value="">Select a member...</option>
                    {otherMembers.map((m) => {
                      const profile = Array.isArray(m.member_profiles)
                        ? m.member_profiles[0]
                        : m.member_profiles;
                      return (
                        <option key={m.profile_id} value={m.profile_id}>
                          {profile?.username || 'Unknown Member'}
                        </option>
                      );
                    })}
                  </select>
                </>
              ) : ownerCanDeleteByLeaving ? (
                <p className={styles.leaveBody}>
                  You are the only member of <strong>{group.name}</strong>. Leaving will
                  delete this group.
                </p>
              ) : (
                <p className={styles.leaveBody}>
                  You're about to leave <strong>{group.name}</strong>. You can always
                  rejoin later.
                </p>
              )}
              <div className={styles.leaveActions}>
                <button
                  className={styles.leaveCancelBtn}
                  onClick={() => {
                    setShowLeaveConfirm(false);
                    setNewOwnerId('');
                  }}
                >
                  Cancel
                </button>
                <button
                  data-testid="leave-confirm-btn"
                  className={styles.leaveConfirmBtn}
                  disabled={ownerMustTransfer && !newOwnerId}
                  style={{
                    opacity: ownerMustTransfer && !newOwnerId ? 0.5 : 1,
                    cursor: ownerMustTransfer && !newOwnerId ? 'not-allowed' : 'pointer',
                  }}
                  onClick={() => {
                    setShowLeaveConfirm(false);
                    setOptimisticJoined(false);
                    leaveMutation.mutate({
                      groupId: id!,
                      ...(ownerMustTransfer && newOwnerId ? { newOwnerId } : {}),
                    });
                  }}
                >
                  Leave Group
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
